import KpiGridHead from "./KpiGridHead";
import KpiGhostRow from "./KpiGhostRow";
import { spanOf } from "./columns";

/**
 * The table shell: sticky header, the caller's rows, and the ghost row that
 * adds a KPI. Loading, failure and empty are rendered here as rows so the
 * header and column widths never jump between states.
 *
 * @param {object} props
 * @param {{ key: string, label: string, width?: number, sub?: object[] }[]} props.columns visible columns
 * @param {boolean} props.loading blocking load — first paint or context switch
 * @param {string} [props.error]
 * @param {() => void} props.onRetry
 * @param {boolean} props.isEmpty no rows after filtering
 * @param {boolean} props.hasRows any saved rows at all, before filtering
 * @param {boolean} props.allSelected
 * @param {boolean} props.someSelected
 * @param {boolean} [props.addDisabled] rows can be edited, but not added
 * @param {(selected: boolean) => void} props.onToggleAll
 * @param {(title: string) => void} props.onSeedRow
 * @param {React.ReactNode} props.children the rows
 */
export default function KpiGrid({
  columns,
  loading,
  error,
  onRetry,
  isEmpty,
  hasRows,
  allSelected,
  someSelected,
  addDisabled,
  onToggleAll,
  onSeedRow,
  children,
}) {
  const columnCount = columns.reduce((sum, column) => sum + spanOf(column), 1);
  return (
    <div className="kgb-scroll">
      <table className="kgb-grid">
        <KpiGridHead
          columns={columns}
          allSelected={allSelected}
          someSelected={someSelected}
          disabled={loading || !!error}
          onToggleAll={onToggleAll}
        />
        <tbody>
          {loading ? (
            <tr className="kgb-staterow">
              <td colSpan={columnCount}>Loading KPIs…</td>
            </tr>
          ) : null}

          {!loading && error ? (
            <tr className="kgb-staterow kgb-staterow--error">
              <td colSpan={columnCount}>
                <span>{error}</span>
                <button type="button" className="kgb-btn" onClick={onRetry}>
                  Try again
                </button>
              </td>
            </tr>
          ) : null}

          {!loading && !error ? children : null}

          {!loading && !error && isEmpty ? (
            <tr className="kgb-staterow">
              <td colSpan={columnCount}>
                {hasRows
                  ? "No KPI matches this filter."
                  : "This module has no KPIs for the selected year yet."}
              </td>
            </tr>
          ) : null}

          {!loading && !error ? (
            <KpiGhostRow columnCount={columnCount} disabled={addDisabled} onSeed={onSeedRow} />
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
